import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Coins, Sparkles, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES = [
  {
    id: 'free',
    name: 'Free Trial',
    price: 0,
    priceLabel: 'Free',
    credits: 50,
    bonus: 0,
    description: 'Try it out with 50 free credits',
    features: ['50 credits', 'Basic features', 'One-time claim'],
    popular: false,
  },
  {
    id: 'starter',
    name: 'Starter Pack',
    price: 5,
    priceLabel: '$5',
    credits: 100,
    bonus: 10,
    description: 'Perfect for getting started',
    features: ['100 credits', '+10 bonus credits', 'Full reader access'],
    popular: false,
  },
  {
    id: 'popular',
    name: 'Popular Pack',
    price: 15,
    priceLabel: '$15',
    credits: 350,
    bonus: 50,
    description: 'Best for regular learners',
    features: ['350 credits', '+50 bonus credits', 'AI assistance', 'Priority support'],
    popular: true,
  },
  {
    id: 'best_value',
    name: 'Best Value',
    price: 30,
    priceLabel: '$30',
    credits: 800,
    bonus: 150,
    description: 'Maximum savings for power users',
    features: ['800 credits', '+150 bonus credits', 'All features', 'Forum perks'],
    popular: false,
  },
];

export default function BuyCredits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [freeClaimed, setFreeClaimed] = useState(false);

  useEffect(() => {
    fetchBalance();
    checkFreeClaimed();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/credits/balance`, { withCredentials: true });
      setCreditBalance(res.data.balance || 0);
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const checkFreeClaimed = async () => {
    try {
      // Check if free credits were already claimed by checking transaction history
      const res = await axios.get(`${BACKEND_URL}/api/credits/history?limit=100`, { withCredentials: true });
      const hasFree = res.data.transactions?.some(t => 
        t.description?.includes('free') || t.transaction_type === 'free_trial'
      );
      setFreeClaimed(hasFree);
    } catch (err) {
      console.error('Error checking free status:', err);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      toast.error('Please log in to purchase credits');
      navigate('/');
      return;
    }

    setLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(
        `${BACKEND_URL}/api/payments/create-checkout`,
        { package_id: packageId, origin_url: originUrl },
        { withCredentials: true }
      );

      if (res.data.status === 'free_claimed') {
        toast.success(res.data.message);
        setFreeClaimed(true);
        fetchBalance();
      } else if (res.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to initiate purchase';
      toast.error(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Coins className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Buy Credits</h1>
          <p className="text-muted-foreground">
            Credits power AI features, TTS, and premium content
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span className="font-medium">Current Balance: {creditBalance} credits</span>
          </div>
        </div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PACKAGES.map((pkg) => (
            <Card 
              key={pkg.id}
              className={`relative ${pkg.popular ? 'border-primary shadow-lg ring-2 ring-primary/20' : ''}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                  Most Popular
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <div className="text-3xl font-bold mt-2">{pkg.priceLabel}</div>
                <CardDescription className="mt-2">{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <span className="text-2xl font-semibold text-primary">
                    {pkg.credits + pkg.bonus}
                  </span>
                  <span className="text-muted-foreground ml-1">credits</span>
                  {pkg.bonus > 0 && (
                    <div className="text-xs text-green-500 mt-1">
                      +{pkg.bonus} bonus credits!
                    </div>
                  )}
                </div>
                
                <ul className="space-y-2">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  variant={pkg.popular ? 'default' : 'outline'}
                  disabled={loading === pkg.id || (pkg.id === 'free' && freeClaimed)}
                  onClick={() => handlePurchase(pkg.id)}
                  data-testid={`buy-${pkg.id}`}
                >
                  {loading === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : pkg.id === 'free' && freeClaimed ? (
                    'Already Claimed'
                  ) : pkg.price === 0 ? (
                    'Claim Free Credits'
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Buy Now
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment Info */}
        <div className="mt-10 text-center text-sm text-muted-foreground">
          <p>Secure payments powered by Stripe. All major credit cards accepted.</p>
          <p className="mt-2">Credits never expire. Use them anytime for AI features.</p>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
